import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/app/Header";
import AiOfficeLink from "../components/app/AiOfficeLink";
import { Radar, Activity, TrendingDown, ClipboardList, Lock, ArrowRight, Gem, AlertTriangle, Sparkles, Target } from "lucide-react";

const ACTIVE_AGENTS = [
  { id: "lead-scoring", name: "Smart Lead Scoring", role: "Reads every conversation. Scores what your CRM can't.", Icon: Radar, to: "/employz/lead-scoring" },
  { id: "goldmine", name: "Repeat-Business Goldmine", role: "Finds paying customers who vanished — and prices what's dormant.", Icon: Gem, to: "/employz/goldmine" },
  { id: "missed-money", name: "Missed Money Finder", role: "Buying-intent replies that were never answered — with receipts.", Icon: AlertTriangle, to: "/employz/missed-money" },
  { id: "field-review", name: "Field Review", role: "AI-proposed CRM field values pulled from conversations. Accept, reject, or let high-confidence ones auto-write.", Icon: Sparkles, to: "/employz/field-review" },
  { id: "lead-hunter", name: "Lead Hunter", role: "Finds real companies with a recent trigger — every claim links to the source page.", Icon: Target, to: "/employz/lead-hunter" },
];

const LOCKED_AGENTS = [
  { id: "pipeline-autopsy", name: "Pipeline Autopsy", role: "Where deals actually die — with names, dates, and one fix per stage.", Icon: Activity },
  { id: "revenue-leak-detector", name: "Revenue Leak Detector", role: "Finds unbilled work, dropped follow-ups, and stalled invoices.", Icon: TrendingDown },
  { id: "daily-briefing", name: "Daily Briefing", role: "The one report your team should read before starting the day.", Icon: ClipboardList },
];

export default function GhlIntelligence() {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-6 md:px-10 pt-8 pb-16">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.28em] text-emerald-400">
            // Employz Intelligence
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl tracking-tighter font-black text-white">
            Agents that <span className="text-amber-400">read</span> your CRM,
            <br />not your dashboard.
          </h1>
          <p className="mt-3 text-zinc-400 max-w-2xl text-sm md:text-base leading-relaxed">
            These agents work directly against your connected CRM sub-account —
            reading conversations, opportunities, and pipeline movement — then write
            reasoned answers back as custom fields so your existing workflows can act on them.
          </p>
          <div className="mt-5">
            <AiOfficeLink variant="card" />
          </div>
        </div>

        {/* Active + locked in the same grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ACTIVE_AGENTS.map((agent) => (
          <Link
            key={agent.id}
            data-testid={`ghl-agent-${agent.id}`}
            to={agent.to}
            className="group relative rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-950/40 via-zinc-950 to-zinc-950 p-5 hover:border-emerald-400/40 hover:shadow-[0_0_0_1px_rgba(52,211,153,0.15)] transition-all"
          >
            <div className="absolute top-4 right-4 text-[9px] uppercase tracking-[0.24em] text-emerald-400 border border-emerald-500/30 rounded-full px-2 py-0.5">
              Active
            </div>
            <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-3">
              <agent.Icon className="w-5 h-5 text-emerald-300" />
            </div>
            <div className="font-display text-xl tracking-tight text-white font-bold">
              {agent.name}
            </div>
            <div className="mt-1.5 text-sm text-zinc-400 leading-relaxed">
              {agent.role}
            </div>
            <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-emerald-300 group-hover:text-emerald-200">
              Open agent <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </Link>
          ))}

          {LOCKED_AGENTS.map((a) => (
            <div
              key={a.id}
              data-testid={`ghl-agent-${a.id}-locked`}
              className="relative rounded-2xl border border-zinc-900 bg-zinc-950/60 p-5"
            >
              <div className="absolute top-4 right-4 text-[9px] uppercase tracking-[0.24em] text-zinc-500 border border-zinc-800 rounded-full px-2 py-0.5">
                Coming to your programme
              </div>
              <div className="w-11 h-11 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-3">
                <a.Icon className="w-5 h-5 text-zinc-500" />
              </div>
              <div className="font-display text-xl tracking-tight text-zinc-300 font-bold">
                {a.name}
              </div>
              <div className="mt-1.5 text-sm text-zinc-500 leading-relaxed">
                {a.role}
              </div>
              <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-zinc-600">
                <Lock className="w-3 h-3" /> Locked
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
